import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { DataTable } from '@/components/ui/data-table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { getTreatmentColumns } from '@/components/treatments/treatment-columns'
import { TreatmentToolbar, type DateFilter } from '@/components/treatments/treatment-toolbar'
import { TreatmentSheet } from '@/components/treatments/treatment-sheet'
import { UnmatchedTreatmentsTab } from '@/components/treatments/unmatched-treatments-tab'
import { useTreatments, useTreatmentCategories, useTreatmentStats } from '@/hooks/useTreatments'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import type { Treatment, TreatmentType } from '@/lib/api-client'
import type { SortingState, RowSelectionState, VisibilityState } from '@tanstack/react-table'
import {
  useReactTable,
  getCoreRowModel,
} from '@tanstack/react-table'
import { List, AlertTriangle } from 'lucide-react'

export const Route = createFileRoute('/treatments')({
  component: TreatmentsPage,
})

// Branch code to ID mapping
const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

function TreatmentsPage() {
  const { branchId } = useBranch()
  const [activeTab, setActiveTab] = React.useState('catalog')

  // Table state
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(20)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  // Hide Type and Status columns by default (can be shown via column selector)
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    type: false,
    isActive: false,
  })

  // Search and filters
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [categoryId, setCategoryId] = React.useState<number | null>(null)
  const [type, setType] = React.useState<TreatmentType | null>(null)
  const [dateFilter, setDateFilter] = React.useState<DateFilter>('week') // Default: This Week

  // Sheet state
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [selectedTreatmentId, setSelectedTreatmentId] = React.useState<number | null>(null)

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1)
  }, [categoryId, type, branchId, dateFilter])

  // Fetch categories for filter dropdown
  const { data: categoriesData } = useTreatmentCategories()
  const categories = categoriesData?.data ?? []

  // Fetch stats for tab badge
  const { data: stats } = useTreatmentStats()
  const unmatchedCount = stats?.uniqueUnmatched ?? 0

  // Get sort params from state (map 'sales' column id back to 'itemCount' for API)
  const sortById = sorting[0]?.id
  const sortBy = (sortById === 'sales' ? 'itemCount' : sortById) as 'name' | 'itemCount' | 'revenue' | undefined
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'

  // Fetch treatments
  const { data, isLoading } = useTreatments({
    page,
    pageSize,
    branchId: BRANCH_IDS[branchId],
    categoryId: categoryId || undefined,
    type: type || undefined,
    search: debouncedSearch || undefined,
    sortBy,
    sortOrder: sorting.length > 0 ? sortOrder : undefined,
    dateFilter: dateFilter !== 'all' ? dateFilter : undefined,
  })

  // Handlers
  const handleView = React.useCallback((treatment: Treatment) => {
    setSelectedTreatmentId(treatment.id)
    setSheetOpen(true)
  }, [])

  const handleEdit = React.useCallback((treatment: Treatment) => {
    setSelectedTreatmentId(treatment.id)
    setSheetOpen(true)
  }, [])

  const handleRowClick = React.useCallback((treatment: Treatment) => {
    handleView(treatment)
  }, [handleView])

  // Memoize columns
  const columns = React.useMemo(
    () => getTreatmentColumns({ onView: handleView, onEdit: handleEdit }),
    [handleView, handleEdit]
  )

  // Create table instance
  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    pageCount: data?.totalPages ?? -1,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function'
        ? updater({ pageIndex: page - 1, pageSize })
        : updater
      setPage(newPagination.pageIndex + 1)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Treatment MDM"
        subtitle={
          activeTab === 'catalog'
            ? data ? `${data.total.toLocaleString()} treatments` : 'Loading...'
            : `${unmatchedCount} unmatched items`
        }
      />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-card border border-input p-1">
            <TabsTrigger
              value="catalog"
              className="flex items-center gap-2 data-[state=active]:bg-icon-beige"
            >
              <List className="h-4 w-4" />
              Master Catalog
            </TabsTrigger>
            <TabsTrigger
              value="unmatched"
              className="flex items-center gap-2 data-[state=active]:bg-icon-beige"
            >
              <AlertTriangle className="h-4 w-4" />
              Unmatched Items
              {unmatchedCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">
                  {unmatchedCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Master Catalog Tab */}
          <TabsContent value="catalog" className="mt-4 space-y-4">
            {/* Toolbar */}
            <TreatmentToolbar
              search={search}
              onSearchChange={setSearch}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              categoryId={categoryId}
              onCategoryChange={setCategoryId}
              type={type}
              onTypeChange={setType}
              categories={categories}
              table={table}
            />

            {/* Table */}
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              isLoading={isLoading}
              pageCount={data?.totalPages}
              pageIndex={page - 1}
              pageSize={pageSize}
              onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
              sorting={sorting}
              onSortingChange={setSorting}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              onRowClick={handleRowClick}
              emptyMessage="No treatments found. Try adjusting your search or filters."
            />
          </TabsContent>

          {/* Unmatched Items Tab */}
          <TabsContent value="unmatched" className="mt-4">
            <UnmatchedTreatmentsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Treatment Detail Sheet */}
      <TreatmentSheet
        treatmentId={selectedTreatmentId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  )
}
